/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { booleanAttribute, Component, input, output } from '@angular/core';
import { EMPTY_STRING } from '@common/tech-shared';
import { cva } from 'class-variance-authority';
import { BadgeComponent, BadgeConfig } from '../badge/badge.component';
import { ButtonComponent } from '../button/button.component';
import { RoutingButtonComponent } from '../routing-button/routing-button.component';

export const overviewListPageHeadingVariants = cva('text-text font-medium', {
  variants: {
    headingLevel: {
      1: 'text-3xl',
      2: 'text-2xl',
      3: 'text-xl',
      4: 'text-lg',
      5: 'text-base',
      6: 'text-base',
    },
  },
  defaultVariants: {
    headingLevel: 1,
  },
});

@Component({
  selector: 'ods-overview-list-page',
  template: `<div class="flex flex-row flex-wrap items-center gap-4">
      <div
        role="heading"
        [attr.aria-level]="headingLevel()"
        [class]="overviewListPageHeadingVariants({ headingLevel: headingLevel() })"
        data-test-id="overview-list-page-heading"
      >
        {{ heading() }}
      </div>
      @if (badgeConfig(); as badge) {
        <ods-badge [name]="badge.text" [color]="badge.color" size="medium" data-test-id="overview-list-page-badge" />
      }
    </div>
    @if (description()) {
      <p class="whitespace-pre-line" data-test-id="overview-list-page-description">{{ description() }}</p>
    }
    @if (addPath()) {
      <ods-routing-button
        [linkPath]="addPath()"
        [isRelativePath]="isRelativePath()"
        [text]="newButtonText()"
        [dataTestId]="buttonDataTestId()"
        [attr.data-test-id]="'overview-list-page-link-button'"
      />
    } @else if (newButtonText()) {
      <ods-button
        size="fit"
        [text]="newButtonText()"
        [dataTestId]="buttonDataTestId()"
        (clicked)="newButtonClick.emit()"
        [attr.data-test-id]="'overview-list-page-action-button'"
      />
    }
    @if (isEmpty()) {
      @if (emptyListText()) {
        <p data-test-id="overview-list-page-empty-list-text">{{ emptyListText() }}</p>
      }
    } @else {
      <ng-content />
    }`,
  imports: [RoutingButtonComponent, ButtonComponent, BadgeComponent],
  host: { class: 'flex flex-col gap-4 text-text' },
})
export class OverviewListPageComponent {
  public readonly heading = input.required<string>();
  public readonly headingLevel = input<1 | 2 | 3 | 4 | 5 | 6>(1);
  public readonly badgeConfig = input<BadgeConfig>();
  public readonly description = input<string>(EMPTY_STRING);
  public readonly emptyListText = input<string>(EMPTY_STRING);
  public readonly newButtonText = input<string>(EMPTY_STRING);
  public readonly addPath = input<string>(EMPTY_STRING);
  public readonly isRelativePath = input(false, { transform: booleanAttribute });
  public readonly isEmpty = input(false, { transform: booleanAttribute });
  public readonly buttonDataTestId = input<string>(EMPTY_STRING);

  public readonly overviewListPageHeadingVariants = overviewListPageHeadingVariants;

  public readonly newButtonClick = output<void>();
}
